import Link from 'next/link'
import { useRouter } from 'next/router'
import styled, { css } from 'styled-components'
import { useGqlTotalCountsByCompanyIdQuery } from 'generated/graphql'
import { useGqlTotalPostsCountsByCompanyIdCommunityQuery } from 'generated/graphql.community'
import {
  COMPANY_INFO_TABS,
  CompanyInfoTabInfo,
  getCompanyInfoActiveTab,
} from '../../constants/detailTabs'

const CompanyInfoDetailTabs = ({ companyId }: { companyId: string }) => {
  const router = useRouter()

  const activeTab = getCompanyInfoActiveTab(router.pathname)

  const { data: countData } = useGqlTotalCountsByCompanyIdQuery({
    variables: { companyId },
    fetchPolicy: 'cache-first',
  })

  const { data: postsCountData } =
    useGqlTotalPostsCountsByCompanyIdCommunityQuery({
      variables: { companyId },
      fetchPolicy: 'cache-first',
      context: { clientName: 'community' },
    })

  const getTabCount = (tab: CompanyInfoTabInfo): number => {
    switch (tab.dataPath) {
      case 'reviews':
        return postsCountData?.reviews.totalCount || 0
      case 'qna':
        return (
          (postsCountData?.qnasMentor.totalCount || 0) +
          (countData?.qnasChat.totalCount || 0)
        )
      default:
        return countData?.[tab.dataPath]?.totalCount || 0
    }
  }

  return (
    <StyledWrapper>
      {Object.entries(COMPANY_INFO_TABS).map(([tabKey, tab]) => {
        const count = getTabCount(tab)

        const href =
          tabKey === 'overview'
            ? `/company-info/${router.query.id}`
            : `/company-info/${router.query.id}/${tabKey}`

        return (
          <Link key={tabKey} href={href} shallow={false} scroll={false}>
            <a className={`tab-button ${activeTab === tabKey ? 'active' : ''}`}>
              <span className="tab-name">{tab.name}</span>
              {count > 0 && <span className="tab-count">{count}</span>}
            </a>
          </Link>
        )
      })}
    </StyledWrapper>
  )
}

export default CompanyInfoDetailTabs

const StyledWrapper = styled.nav`
  ${({ theme }) => css`
    width: 1100px;
    height: 67px;
    display: flex;
    flex-direction: row;
    gap: 24px;

    .tab-button {
      background: none;
      border: none;
      padding: 16px 8px;
      cursor: pointer;
      display: flex;
      align-items: center;
      gap: 4px;
      position: relative;
      transition: all 0.2s ease;

      &:hover {
        .tab-name {
          color: ${theme.color.primary_2};
        }
      }

      &.active {
        .tab-name {
          color: ${theme.color.primary_2};
          font-weight: 600;
        }

        &::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background-color: ${theme.color.primary_2};
        }
      }
    }

    .tab-name {
      font-size: 16px;
      font-weight: 500;
      transition: color 0.2s ease;
    }

    .tab-count {
      font-size: 12px;
      font-weight: 500;
      margin-left: 2px;
      background-color: ${theme.background.bg_2};
      padding: 2px 8px;
      border-radius: 10px;
    }

    .active .tab-count {
      background-color: ${theme.color.primary_2};
      color: #fff;
    }
  `}
`
